import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Component, ChangeDetectionStrategy, OnInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { ListTableFiltersWrapperParent } from '@app/wrappers/pages/list-table-filters-wrapper/list-table-filters-wrapper-parent';
import { IMsfFormBuilder } from '@root/src/app/components/form-builder/form-builder';
import { ListTableFiltersWrapper } from '@app/wrappers/pages/list-table-filters-wrapper/list-table-filters-wrapper';
import { RequestParams } from '@components/select/resource-component';
import { UserService } from '@6grain/masofa-monolith-api-client/identity';
import { DataTableComponent } from '@components/data-table/data-table.component';
import { DateFormatLangPipe } from '@app/utils/pipes/date-format-lang-pipe';
import { BusinessLogicLoggerService } from '@6grain/masofa-monolith-api-client/common';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-logging-page',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ButtonModule,
    DialogModule,
    TooltipModule,
    TagModule,
    ListTableFiltersWrapper,
    DataTableComponent,
  ],
  templateUrl: './logging-page.html',
  styleUrl: './logging-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DateFormatLangPipe, DatePipe],
})
export class LoggingPageComponent extends ListTableFiltersWrapperParent<any, any> implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  override filtersForms: IMsfFormBuilder[] = [];
  override getAllMethod = 'commonBusinessLogicLoggerGetByQueryPost';
  override getTotalMethod = 'commonBusinessLogicLoggerGetTotalCountPost';
  override storageKey = 'admin_logging_list_table';

  defaultParams: RequestParams = {
    take: 10,
    offset: 0,
    filters: [],
  };

  showLogDetailsModal: boolean = false;
  selectedLogDetails: any[] = [];
  showFullMessageModal: boolean = false;
  fullMessageContent: string = '';
  tableKey: number = 0;
  totalRecords: number = 0;
  usersList: any[] = [];
  usersMap: Map<string, string> = new Map();
  allUserNames: string[] = [];
  subsystemsList: string[] = [];
  loadingSubsystems: boolean = false;
  loadingUsers: boolean = false;

  filterKeywords: string[] = [];
  parentRoute: string = '/admin';

  private readonly MESSAGE_MAX_LENGTH = 50;

  serviceAdapter: any;

  constructor(
    public override service: BusinessLogicLoggerService,
    public userService: UserService,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
  ) {
    super();
  }

  override ngOnInit(): void {
    const storageKey = this.storageKey;
    if (storageKey) {
      localStorage.removeItem(storageKey);
    }

    const routeData = this.route.snapshot.data;
    if (routeData['parentRoute']) {
      this.parentRoute = routeData['parentRoute'];
    }
    if (routeData['filterKeywords'] && Array.isArray(routeData['filterKeywords'])) {
      this.filterKeywords = routeData['filterKeywords'];
    }

    const initialFilters: any[] = [];

    if (this.filterKeywords.length > 0) {
      initialFilters.push({
        name: 'subsystem',
        value: this.filterKeywords,
        operator: 6,
      });
    }

    this.filtersParams = initialFilters;
    this.defaultParams = {
      take: 10,
      offset: 0,
      filters: initialFilters,
    };

    super.ngOnInit();

    this.initFiltersForm();

    this.loadUsers();
    this.loadSubsystems();
  }

  private initFiltersForm(): void {
    this.filtersForms = [
      {
        type: 'date',
        name: 'timestampFrom',
        title: 'DateTimeFrom',
        placeholder: 'SelectStartDateTime',
        options: {
          date: {
            selectionMode: 'single',
            showTime: true,
            showSeconds: true,
          },
        },
      },
      {
        type: 'date',
        name: 'timestampTo',
        title: 'DateTimeTo',
        placeholder: 'SelectEndDateTime',
        options: {
          date: {
            selectionMode: 'single',
            showTime: true,
            showSeconds: true,
          },
        },
      },
      {
        type: 'select',
        name: 'subsystem',
        title: 'Subsystem',
        placeholder: 'SelectSubsystem',
        getSelectOptions: () => this.getSubsystemOptions(),
        options: {
          select: {
            optionLabel: 'label',
            optionValue: 'value',
            translateBy: 'none',
            isUser: false,
            loading: () => this.loadingSubsystems,
          },
        },
      },
      {
        type: 'select',
        name: 'userName',
        title: 'User',
        placeholder: 'SelectUser',
        getSelectOptions: () => this.getUserOptions(),
        options: {
          select: {
            optionLabel: 'label',
            optionValue: 'value',
            translateBy: 'none',
            isUser: false,
            loading: () => this.loadingUsers,
          },
        },
      },
      {
        type: 'select',
        name: 'eventClass',
        title: 'EventClass',
        placeholder: 'SelectEventClass',
        getSelectOptions: () => this.getEventClassOptions(),
        options: {
          select: {
            optionLabel: 'label',
            optionValue: 'value',
            translateBy: 'none',
          },
        },
      }     
    ];
    this.filtersForms = [...this.filtersForms];
    this.cdr.detectChanges();
  }

  override onRowClick(log: any): void {
    return;
  }

  onViewDetails(row: any): void {
    const id = (row?.id ?? row?.Id) as string | undefined;
    if (!id) {
      return;
    }
    this.loadLogDetails(id);
  }

  onFilterModelChange(filterModel: any): void {
    this.filterModel = { ...filterModel };
  }

  onFilterSaved(savedFilters: any): void {
    if (this.savedFilterModel) {
      this.filterModel = { ...this.savedFilterModel };
      this.cdr.detectChanges();
    }
  }

  private savedFilterModel: any = null;

  updateFilters(filters: any): void {
    this.savedFilterModel = { ...this.filterModel };

    // FilterOperator enum values:
    // 0 = Equals
    // 1 = NotEquals
    // 2 = GreaterThan
    // 3 = GreaterThanOrEqual
    // 4 = LessThan
    // 5 = LessThanOrEqual
    // 6 = Contains
    // 7 = StartsWith
    // 8 = EndsWith
    const backendFilters: any[] = [];

    if (filters.timestampFrom || filters.timestampTo) {
      if (filters.timestampFrom && filters.timestampTo) {
        const startDate =
          filters.timestampFrom instanceof Date ? filters.timestampFrom : new Date(filters.timestampFrom);

        const endDate = filters.timestampTo instanceof Date ? filters.timestampTo : new Date(filters.timestampTo);

        backendFilters.push({
          name: 'timestamp',
          value: [startDate.toISOString(), endDate.toISOString()],
        });
      } else if (filters.timestampFrom) {
        const startDate =
          filters.timestampFrom instanceof Date ? filters.timestampFrom : new Date(filters.timestampFrom);

        const maxDate = new Date('2099-12-31T23:59:59.999Z');

        backendFilters.push({
          name: 'timestamp',
          value: [startDate.toISOString(), maxDate.toISOString()],
        });
      } else if (filters.timestampTo) {
        const minDate = new Date('1900-01-01T00:00:00.000Z');

        const endDate = filters.timestampTo instanceof Date ? filters.timestampTo : new Date(filters.timestampTo);

        backendFilters.push({
          name: 'timestamp',
          value: [minDate.toISOString(), endDate.toISOString()],
        });
      }
    }

    if (filters.subsystem) {
      backendFilters.push({
        name: 'subsystem',
        value: filters.subsystem,
      });
    } else if (this.filterKeywords.length > 0) {
      backendFilters.push({
        name: 'subsystem',
        value: this.filterKeywords,
        operator: 6,
      });
    }

    if (filters.userName) {
      backendFilters.push({
        name: 'userName',
        value: filters.userName,
      });
    }
       
    if (filters.eventClass) {         
      backendFilters.push({
        name: 'eventClass',
        value: filters.eventClass,
      });
    }

    this.filtersParams = backendFilters;
    this.defaultParams = {
      take: 10,
      offset: 0,
      filters: backendFilters,
    };

    this.toggleFilter = !this.toggleFilter;
    this.cdr.detectChanges();
  }

  override clearFilters(): void {
    this.savedFilterModel = null;

    const defaultFilters: any[] = [];
    if (this.filterKeywords.length > 0) {
      defaultFilters.push({
        name: 'subsystem',
        value: this.filterKeywords,
        operator: 0,
      });
    }

    this.filtersParams = defaultFilters;
    this.defaultParams = {
      take: 10,
      offset: 0,
      filters: defaultFilters,
    };

    this.filterModel = {};

    this.toggleFilter = !this.toggleFilter;
    this.cdr.detectChanges();
  }

  getLogLevelSeverity(level: string): string {
    const severityMap: Record<string, string> = {
      Debug: 'secondary',
      Information: 'success',
      Warning: 'warn',
      Error: 'danger',
      Critical: 'info',
    };
    return severityMap[level] || 'secondary';
  }

  isMessageLong(message: string): boolean {
    return !!(message && message.length > this.MESSAGE_MAX_LENGTH);
  }

  getTruncatedMessage(message: string): string {
    if (!message) return '';
    return this.isMessageLong(message) ? message.substring(0, this.MESSAGE_MAX_LENGTH) : message;
  }

  showFullMessage(message: string): void {
    this.fullMessageContent = message;
    this.showFullMessageModal = true;
  }

  copyFullMessage(): void {
    this.copyToClipboard(this.fullMessageContent);
  }

  formatJsonMessage(message: string): string {
    try {
      const parsed = JSON.parse(message);
      return JSON.stringify(parsed, null, 2);
    } catch (e) {
      return message;
    }
  }

  private copyToClipboard(text: string): void {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(text)
        .then(() => {})
        .catch((err) => {
          console.error('Failed to copy text: ', err);
        });
    } else {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
      } catch (err) {
        console.error('Failed to copy text: ', err);
      } finally {
        document.body.removeChild(textArea);
      }
    }
  }

  private loadLogDetails(id: string | number): void {
    this.selectedLogDetails = [];
    this.showLogDetailsModal = false;
    this.cdr.detectChanges();

    this.service.commonBusinessLogicLoggerGetLogMessagesByCallStackIdGet(id as string).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.selectedLogDetails = (res || []).map((m: any) => ({
          createdAt: m?.createAt ?? m?.CreateAt ?? m?.createdAt,
          message: m?.message ?? m?.Message,
          logLevelType: m?.logLevelType ?? m?.LogLevelType,
          path: m?.path ?? m?.Path,
          order: m?.order ?? m?.Order,
        }));

        console.log('Data mapped, opening modal...');
        this.tableKey++;
        this.showLogDetailsModal = true;
        this.cdr.detectChanges();

        setTimeout(() => {
          this.cdr.detectChanges();
        }, 50);
      },
      error: (err) => {
        console.error('Error loading log details:', err);
      },
    });
  }

  loadUsers(): void {
    this.loadingUsers = true;
    this.initFiltersForm();

    this.userService
      .identityUserGetByQueryPost({
        take: 1000,
        offset: 0,
        filters: [],
      })
      .subscribe({
        next: (users: any[]) => {
          this.usersList = users || [];

          this.usersMap.clear();
          this.usersList.forEach((user) => {
            if (user.id && user.userName) {
              this.usersMap.set(user.id, user.userName);
            }
          });
          this.loadingUsers = false;
          this.initFiltersForm();
          console.log('Users loaded:', this.usersList.length);
        },
        error: (error) => {
          console.error('Error loading users:', error);
          this.usersList = [];
          this.loadingUsers = false;
          this.initFiltersForm();
        },
      });
  }

  getUserNameById(userId: string): string {
    if (!userId) return '—';

    if (!userId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
      return userId;
    }

    const user = this.usersList.find((u) => u.id === userId || u.userName === userId);
    if (user && user.userName) {
      return user.userName;
    } else if (user.email) {
      return user.email;
    }

    return this.usersMap.get(userId) || userId;
  }

  getUserOptions(): { label: string; value: string }[] {
    return this.usersList.map((user) => {
      let displayName = '';
      if (user.userName) {
        displayName = user.userName;
      } else if (user.email) {
        displayName = user.email;
      } else {
        displayName = user.id || 'Unknown User';
      }

      return {
        label: displayName,
        value: user.userName || user.email || user.id,
      };
    });
  }

  loadSubsystems(): void {
    this.loadingSubsystems = true;
    this.initFiltersForm();

    this.service.commonBusinessLogicLoggerGetSubsystemsGet().pipe(takeUntil(this.destroy$)).subscribe({
      next: (subsystems: string[]) => {
        let loadedSubsystems = subsystems || [];

        if (this.filterKeywords.length > 0) {
          loadedSubsystems = loadedSubsystems.filter((sub) =>
            this.filterKeywords.some((keyword) => sub.includes(keyword)),
          );
        }

        this.subsystemsList = loadedSubsystems;
        this.loadingSubsystems = false;
        this.initFiltersForm();
      },
      error: (error) => {
        console.error('Error loading subsystems:', error);
        this.subsystemsList = [];
        this.loadingSubsystems = false;
        this.initFiltersForm();
      },
    });
  }

  getSubsystemOptions(): { label: string; value: string }[] {
    return this.subsystemsList.map((subsystem) => ({
      label: subsystem,
      value: subsystem,
    }));
  }

  getEventClassOptions(): { label: string; value: string }[] {
    return [
      { label: 'Debug', value: 'Debug' },
      { label: 'Information', value: 'Information' },
      { label: 'Warning', value: 'Warning' },
      { label: 'Error', value: 'Error' },
      { label: 'Critical', value: 'Critical' },
    ];
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  } 
}
